import { del as idbDel, get as idbGet, set as idbSet } from 'idb-keyval';
import { create } from 'zustand';
import type { PersistStorage, StorageValue } from 'zustand/middleware';
import { devtools, persist } from 'zustand/middleware';

const LOGO_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const MAX_LOGO_ENTRIES = 10;

const LOGO_CACHE_STORAGE_KEY = 'easyclaim-logo-cache';

// v0 persisted `Blob`s, which WebKit on iOS (Chrome/WKWebView) often cannot read back.
const LOGO_CACHE_STORAGE_VERSION = 1;

interface LogoCacheEntry {
	dataUrl: string;
	expiresAt: number;
}

interface PersistedLogoState {
	entriesByCaseId: Record<string, LogoCacheEntry>;
}

interface LogoState extends PersistedLogoState {
	setLogo: (caseId: string, dataUrl: string) => void;
	clearLogo: (caseId: string) => void;
	clearAll: () => void;
}

function isExpired(entry: LogoCacheEntry | undefined): boolean {
	return !entry || entry.expiresAt <= Date.now();
}

function pruneEntries(
	entriesByCaseId: Record<string, LogoCacheEntry>
): Record<string, LogoCacheEntry> {
	const nextEntries = Object.fromEntries(
		Object.entries(entriesByCaseId).filter(([, entry]) => !isExpired(entry))
	);
	const overflow = Object.keys(nextEntries).length - MAX_LOGO_ENTRIES;

	if (overflow <= 0) {
		return nextEntries;
	}

	const oldestFirst = Object.entries(nextEntries).sort(([, a], [, b]) => a.expiresAt - b.expiresAt);

	for (let i = 0; i < overflow; i++) {
		delete nextEntries[oldestFirst[i][0]];
	}

	return nextEntries;
}

const idbPersistStorage: PersistStorage<PersistedLogoState> = {
	getItem: async (name) => {
		const value = await idbGet<StorageValue<PersistedLogoState>>(name);

		return value ?? null;
	},
	setItem: (name, value) => idbSet(name, value),
	removeItem: (name) => idbDel(name),
};

export const useLogoStore = create<LogoState>()(
	devtools(
		persist(
			(set) => ({
				entriesByCaseId: {},
				setLogo: (caseId, dataUrl) => {
					set(
						(state) => ({
							entriesByCaseId: pruneEntries({
								...state.entriesByCaseId,
								[caseId]: { dataUrl, expiresAt: Date.now() + LOGO_TTL_MS },
							}),
						}),
						undefined,
						'setLogo'
					);
				},
				clearLogo: (caseId) => {
					set(
						(state) => {
							const entriesByCaseId = { ...state.entriesByCaseId };

							delete entriesByCaseId[caseId];

							return { entriesByCaseId };
						},
						undefined,
						'clearLogo'
					);
				},
				clearAll: () => {
					set({ entriesByCaseId: {} }, undefined, 'clearAll');
				},
			}),
			{
				name: LOGO_CACHE_STORAGE_KEY,
				storage: idbPersistStorage,
				version: LOGO_CACHE_STORAGE_VERSION,
				migrate: () => ({ entriesByCaseId: {} }),
				partialize: (state) => ({ entriesByCaseId: state.entriesByCaseId }),
				onRehydrateStorage: () => (state, error) => {
					if (error || !state) {
						return;
					}

					useLogoStore.setState({ entriesByCaseId: pruneEntries(state.entriesByCaseId) });
				},
			}
		),
		{ name: 'LogoStore' }
	)
);

export const useLogoFromCache = (caseId: string | undefined) =>
	useLogoStore((state) => {
		const entry = caseId ? state.entriesByCaseId[caseId] : undefined;

		return entry && !isExpired(entry) ? entry.dataUrl : undefined;
	});
